import { z } from 'zod';

import { distUI, partnerSvcApi, envs, productSvcApi, accountSvcApi, billingSvcApi } from '@ctct/infrastructure-layer';

import { BillingAccountExtendedSchema } from './schemas/billing-account-extended.zod';
import { CustomerSchema } from './schemas/customer.zod';
import { EmailAddressSchema } from './schemas/email-address.zod';
import { PartnerSummarySchema } from './schemas/partner-summary.zod';
import { PartnerOwnerSummarySchema, PlanDetailSchema, UserRoleSchema } from './schemas/plan-detail.zod';
import { SuspendResponseSchema } from './schemas/suspend.zod';
import { UpgradePlanPromotionSchema } from './schemas/upgrade-plan-promotion.zod';
import { BillingAccountExtended, Customer, PartnerSummary, Role, Suspend } from '../../value-objects';
import { EmailAddress } from '../../value-objects/email-address';
import { PlanDetailVO } from '../../value-objects/plan-detail';
import { UpgradePlanPromotionVO } from '../../value-objects/upgrade-plan-promotion';
import { planRepo } from '../plan-repo';
import { zodService } from '../ZodService';

const DISABLE_REASON_ID_OVERDUE_BALANCE = 28;

class AccountRepo {
  public async getPartnerName() {
    let response;

    if (envs.isSecure()) {
      const partnerSummary = await this.getPartnerSummary();

      response = partnerSummary.getPartnerName();
    } else {
      response = await distUI.getPartnerName();
    }

    const data = zodService.safeParseString(response);

    return data;
  }

  public async getPartnerSummary() {
    const response = await partnerSvcApi.getSummary();
    const data = zodService.safeParse(PartnerSummarySchema, response.data);

    return new PartnerSummary(data);
  }

  public async getPartnerOwnerSummary() {
    const distUiResponse = await distUI.getPartnerOwnerSummary();

    const data = zodService.safeParse(PartnerOwnerSummarySchema, distUiResponse);

    return data;
  }

  public async getUserId() {
    const distUiResponse = await distUI.getUserId();

    const data = zodService.safeParseString(distUiResponse);

    return data;
  }

  public async getAccountId() {
    let response;

    if (envs.isSecure()) {
      const extendedBillingAccount = await this.getExtendedBillingAccount();

      response = extendedBillingAccount.getCustomerId();
    } else {
      response = await distUI.getAccountId();
    }

    const data = zodService.safeParseNumber(response);

    return data;
  }

  public async getIsTrialer() {
    let response;

    if (envs.isSecure()) {
      const planGroup = await planRepo.getPlanGroup('USD', 0);

      response = planGroup.isTrialer();
    } else {
      response = await distUI.getIsTrialer();
    }

    const data = zodService.safeParseBool(response);

    return data;
  }

  public async getIsCanceled() {
    const distUiResponse = await distUI.getIsCanceled();

    const data = zodService.safeParseBool(distUiResponse);

    return data;
  }

  public async getIsSuspended() {
    const distUiResponse = await distUI.getIsSuspended();

    const data = zodService.safeParseBool(distUiResponse);

    return data;
  }

  public async getPlanDetail() {
    const { data: productSvcApiResponse } = await productSvcApi.getPlan();

    const data = zodService.safeParse(PlanDetailSchema, productSvcApiResponse);

    return new PlanDetailVO(data);
  }

  public async getRole() {
    let response;

    if (envs.isSecure()) {
      const accountSvcApiResponse = await accountSvcApi.getCurrentAccountRoles();

      response = accountSvcApiResponse.data?.[0]?.roleId;
    } else {
      response = await await distUI.getRole();
    }

    const data = zodService.safeParse(UserRoleSchema, response);

    return new Role(data);
  }

  public async getIsSingleBilled() {
    const response = await distUI.getIsSingleBilled();

    const isSingleBilled = zodService.safeParseBool(response);

    return isSingleBilled;
  }

  public async getHasOverdue() {
    const productAccount = await distUI.getProductAccount();

    return (productAccount.disableReasons || []).some(
      (reason) => reason && typeof reason.id === 'number' && reason.id === DISABLE_REASON_ID_OVERDUE_BALANCE
    );
  }

  public async getExtendedBillingAccount() {
    const response = await billingSvcApi.getAccount();

    const data = zodService.safeParse(BillingAccountExtendedSchema, response.data);

    return new BillingAccountExtended(data);
  }

  public async getCustomerWithPhysicalAddress() {
    const response = await accountSvcApi.getCustomerWithPhysicalAddress();

    const data = zodService.safeParse(CustomerSchema, response.data);

    return new Customer(data);
  }

  public async getSuspensionInfo() {
    const response = await billingSvcApi.getSuspensionInfo();

    const data = zodService.safeParse(SuspendResponseSchema, response.data);

    return new Suspend(data);
  }

  public async getUpgradePlanPromotion() {
    const response = await billingSvcApi.getRecommendedUpgradePlanPromotion();

    const data = zodService.safeParse(UpgradePlanPromotionSchema, response.data);

    return new UpgradePlanPromotionVO(data);
  }

  public async getEmailAddresses() {
    const response = await accountSvcApi.listEmailAddresses();

    const data = zodService.safeParse(z.array(EmailAddressSchema), response.data);

    return data.map((emailData) => new EmailAddress(emailData));
  }

  public async sendVerificationEmail(emailId: number) {
    return accountSvcApi.sendVerificationEmail(emailId);
  }
}

export const accountRepo = new AccountRepo();
